/**
 * Shared percentage-bar cell for the PO Lines grid.
 *
 * Extracted (CR-7163) so "% Delivered" and the supplier-side "Fulfilment %"
 * render identically — same thresholds, same colours, same tabular alignment —
 * rather than drifting as two hand-copied blocks.
 */

/**
 * Percentage for a bar, floored so it can only read 100% when the underlying
 * comparison is genuinely satisfied.
 *
 * `Math.round` would show 100% at 99.5%, which on the Fulfilment % column
 * would disagree with the grey-out rule (`warehouse >= outstanding`): a line
 * at 996/1000 rounds to 100% but does not grey. Flooring keeps the bar and
 * the row state consistent — 100% appears only when `value >= total`.
 *
 * @param value - Numerator (e.g. qty in the warehouse)
 * @param total - Denominator (e.g. qty outstanding)
 * @returns An integer 0-100
 */
export function barPercent(value: number, total: number): number {
  if (total <= 0) return 0;
  if (value >= total) return 100;
  return Math.max(0, Math.min(Math.floor((value / total) * 100), 99));
}

function barColor(pct: number): string {
  if (pct >= 100) return 'bg-emerald-500';
  if (pct >= 50) return 'bg-blue-500';
  if (pct > 0) return 'bg-amber-500';
  return 'bg-gray-200';
}

interface ProgressCellProps {
  value: number;
  total: number;
  /**
   * Rendered instead of the bar when there is nothing to measure against
   * (total <= 0). Omit to render a zero-width bar.
   */
  emptyLabel?: string;
}

/**
 * A `<td>` containing a labelled progress bar, or `emptyLabel` when the
 * denominator is zero.
 *
 * Callers render this inside an array, so they must set React's `key` on the
 * element itself — a `key` on the `<td>` returned from here does NOT satisfy
 * list reconciliation and produces a "unique key prop" warning.
 */
export function ProgressCell({ value, total, emptyLabel }: ProgressCellProps) {
  if (total <= 0 && emptyLabel) {
    return (
      <td className="py-3 px-4 text-center">
        <span className="text-xs text-muted-foreground">{emptyLabel}</span>
      </td>
    );
  }

  const pct = barPercent(value, total);
  return (
    <td className="py-3 px-4">
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden" style={{ minWidth: '50px' }}>
          <div className={`h-full rounded-full ${barColor(pct)}`} style={{ width: `${pct}%` }} />
        </div>
        <span
          className={`text-xs font-medium tabular-nums w-10 text-right ${
            pct >= 100 ? 'text-emerald-600' : 'text-muted-foreground'
          }`}
        >
          {pct}%
        </span>
      </div>
    </td>
  );
}
